/**
 * @file
 * Shared helpers for the modern SRP filter controls.
 */

// Parameters that only ever hold a single scope value, never a visitor choice.
export const LOCKED_PARAMS = ['sale_type', 'organization'];

/**
 * Read the current search parameters from the address bar.
 *
 * The plugin writes `dsp_<key>=a,b`, but links from elsewhere on the site may
 * use `dsp_<key>[]=a&dsp_<key>[]=b`, so both are folded into a comma list.
 */
export function getUrlParams(url = window.location) {
  const params = {};
  new URL(url).searchParams.forEach((value, key) => {
    if (!key.startsWith('dsp_') || value === '') {
      return;
    }
    const name = key.replace(/^dsp_/, '').replace(/\[\]$/, '');
    params[name] = params[name] ? `${params[name]},${value}` : value;
  });
  return params;
}

export function splitList(value) {
  if (value === null || value === undefined || value === '') {
    return [];
  }
  if (Array.isArray(value)) {
    return value.map(String).filter(Boolean);
  }
  return String(value)
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

/**
 * The value sent to the API for an option. Mirrors the plugin's
 * `Checkboxes.getOptionKey()` so URLs stay interchangeable between layouts.
 */
export function optionKey(param, option) {
  let key;
  if (param === 'transmission_type') {
    key = option.name ?? option.id;
  } else {
    key = option.slug ?? option.id ?? option.name;
  }

  // The 'other-unsure' slug often does not work.
  if (key === 'other-unsure' && option.id) {
    key = option.id;
  }

  return String(key);
}

export function optionLabel(param, option) {
  if (param === 'ext_colour' || param === 'int_colour') {
    return String(option.name ?? option.id);
  }
  const label = option.indented_name ?? option.name ?? option.id;
  return String(label).replace(/^-\s*/, '');
}

/**
 * Whether an option is one of the selected values. Ids are always accepted for
 * backwards compatibility, the same as the plugin's checkboxes.
 */
export function isSelected(param, option, selected) {
  if (!selected.length) {
    return false;
  }
  if (selected.includes(optionKey(param, option))) {
    return true;
  }
  return option.id !== undefined && selected.includes(String(option.id));
}

export function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function formatNumber(value) {
  return Number(value).toLocaleString(document.documentElement.lang || 'en-CA');
}

export function sprintf(template, ...values) {
  let index = 0;
  return String(template).replace(/%s/g, () => values[index++] ?? '');
}

// Colour families with their base colour words first and the descriptive OEM
// paint words second. Base words are checked across every family before any
// descriptive word, so "Black Pearl" is black and "Platinum White" is white.
const COLOUR_FAMILIES = [
  ['white', '#ffffff', ['white', 'ivory'], ['oxford', 'pearl', 'snow', 'frost', 'platinum']],
  ['black', '#111827', ['black'], ['ebony', 'onyx', 'shadow', 'jet', 'obsidian', 'agate']],
  ['silver', '#c0c4ca', ['silver'], ['iconic', 'aluminum', 'ingot']],
  [
    'grey',
    '#6b7280',
    ['grey', 'gray'],
    [
      'carbon',
      'graphite',
      'avalanche',
      'magnetic',
      'slate',
      'charcoal',
      'gunmetal',
      'smoke',
      'stone',
      'titanium',
    ],
  ],
  ['red', '#b91c1c', ['red'], ['ruby', 'burgundy', 'maroon', 'hot pepper', 'cherry', 'crimson']],
  [
    'blue',
    '#1d4ed8',
    ['blue'],
    ['atlas', 'velocity', 'navy', 'antimatter', 'azure', 'cobalt', 'kona'],
  ],
  ['green', '#15803d', ['green'], ['eruption', 'cactus', 'olive', 'sage', 'forest', 'emerald']],
  ['orange', '#ea580c', ['orange'], ['twister', 'cyber']],
  ['yellow', '#facc15', ['yellow', 'gold'], ['grabber', 'sunburst']],
  [
    'brown',
    '#78350f',
    ['brown', 'bronze', 'beige', 'tan'],
    ['copper', 'mocha', 'espresso', 'saddle', 'sand', 'desert', 'khaki', 'caramel'],
  ],
  ['purple', '#7e22ce', ['purple', 'violet'], ['plum']],
];

/**
 * Best-guess swatch colour for a free-text colour name.
 *
 * This is display only: filtering still uses the exact colour name, because the
 * API has no normalised colour family to filter on.
 */
export function colourSwatch(name) {
  const words = String(name)
    .toLowerCase()
    .split(/[^a-z]+/)
    .filter(Boolean);
  const text = words.join(' ');
  const matches = (term) => (term.includes(' ') ? text.includes(term) : words.includes(term));

  for (const pass of [2, 3]) {
    for (const family of COLOUR_FAMILIES) {
      if (family[pass].some(matches)) {
        return { family: family[0], hex: family[1] };
      }
    }
  }
  return null;
}
